import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { CitationBadge } from 'components/ui/CitationBadge'
import { CitationSource } from 'types/chat-message'

type ToolSourceRowProps = {
  sourceKey: string
  source: CitationSource
}

const ROW_CLASSES = 'flex items-center gap-1.5 text-xs truncate'

const formatValue = (value: unknown): string => {
  if (value == null) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * A cited agentic tool call (type: 'tool'). Compact by default; when the call carries
 * arguments (e.g. the Qlik agent's { appId, expression }) the row expands into a small
 * "provenance receipt" so the user can read — and copy — the exact query behind the data.
 * With no params it degrades to the same bare tool-name row as before.
 */
export const ToolSourceRow = ({ sourceKey, source }: ToolSourceRowProps) => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const badge = <CitationBadge label={sourceKey} variant="neutral" size="sm" />
  const gear = <OdinIcon icon={['fas', 'gear']} className="h-3 w-3 shrink-0 text-muted-foreground" />
  const name = <span className="truncate font-mono">{source.name}</span>

  const params = source.params ?? {}
  const entries = Object.entries(params)

  if (entries.length === 0) {
    return (
      <span className={`${ROW_CLASSES} text-muted-foreground`}>
        {badge}
        {gear}
        {name}
      </span>
    )
  }

  const { expression, ...rest } = params as { expression?: unknown } & Record<string, unknown>
  const restEntries = Object.entries(rest)
  const canCopy = expression != null && typeof navigator !== 'undefined' && !!navigator.clipboard

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(expression))
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      /* clipboard unavailable — leave the code visible for manual copy */
    }
  }

  return (
    <details className="group">
      <summary
        className={`${ROW_CLASSES} cursor-pointer list-none text-muted-foreground [&::-webkit-details-marker]:hidden`}
      >
        {badge}
        {gear}
        {name}
        <OdinIcon
          icon={['fas', 'chevron-right']}
          className="h-2.5 w-2.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
        />
      </summary>

      <div className="mt-1 ml-6 rounded border border-border p-2">
        {restEntries.length > 0 && (
          <dl className="flex flex-col gap-1">
            {restEntries.map(([key, value]) => (
              <div key={key} className="flex gap-2 text-xs leading-relaxed">
                <dt className="shrink-0 font-mono font-semibold text-muted-foreground">{key}</dt>
                <dd className="min-w-0 break-all text-foreground">{formatValue(value)}</dd>
              </div>
            ))}
          </dl>
        )}

        {expression != null && (
          <div className={restEntries.length > 0 ? 'mt-2' : undefined}>
            <div className="mb-1 flex items-center justify-between">
              <span className="font-mono text-xs font-semibold text-muted-foreground">expression</span>
              {canCopy && (
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-primary"
                >
                  <OdinIcon icon={['fas', copied ? 'check' : 'copy']} className="h-3 w-3" />
                  {copied ? t('copied') : t('copy')}
                </button>
              )}
            </div>
            <code className="block overflow-x-auto whitespace-pre-wrap break-words rounded bg-muted p-2 font-mono text-xs">
              {String(expression)}
            </code>
          </div>
        )}
      </div>
    </details>
  )
}
